/**
 * Gathering surfaces — the pure bits the page, the builder and the manager share
 * (design_handoff_gatherings README + IMPLEMENTATION.md, Oct 2026). Dates are grouped on the
 * LOCAL day (the UTC date mis-bucketed events near the timezone boundary); money is cents.
 */
import { money, plural } from '../../utils/surfaceFormat';

const d = (iso) => {
  if (!iso) return null;
  const dt = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

/** LOCAL day key, `2026-6-10` (month is 0-based — kept for the existing `day-…` testids). */
export const dayKey = (iso) => { const dt = d(iso); return dt ? `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}` : ''; };

/** `FRI 23` — the day strip cell. */
export const dayShort = (iso) => { const dt = d(iso); return dt ? `${dt.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} ${dt.getDate()}` : ''; };

/** `FRIDAY, OCT 23` — the agenda's day header and the builder's lane header. */
export const dayLong = (iso) => { const dt = d(iso); return dt ? dt.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase() : ''; };

/** Local midnight of the day an ISO stamp falls on. */
const startOfDay = (iso) => { const dt = d(iso); return dt ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) : null; };

/**
 * Every local day from `startsAt` to `endsAt` inclusive (capped), as `{ key, date, short, long }`.
 * A `YYYY-MM-DD` date-input value is read as a local date, not UTC midnight.
 */
export function rangeDays(startsAt, endsAt, cap = 7) {
  const asLocal = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v);
  const a = startOfDay(asLocal(startsAt));
  const b = startOfDay(asLocal(endsAt || startsAt)) || a;
  if (!a || !b || b < a) return [];
  const out = [];
  const cur = new Date(a);
  for (let i = 0; i < cap && cur <= b; i += 1) {
    out.push({ key: dayKey(cur), date: new Date(cur), short: dayShort(cur), long: dayLong(cur) });
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** `THE WHOLE WEEKEND` only when the run spans 2+ days and touches a Saturday or Sunday. */
export function wholeLabel(startsAt, endsAt) {
  const a = d(startsAt);
  const b = d(endsAt || startsAt);
  if (!a || !b) return 'THE WHOLE GATHERING';
  if (dayKey(a) === dayKey(b)) return 'THE WHOLE GATHERING';
  let touchesWeekend = false;
  const cur = new Date(a);
  for (let i = 0; i < 8 && cur <= b; i += 1) {
    if (cur.getDay() === 0 || cur.getDay() === 6) touchesWeekend = true;
    cur.setDate(cur.getDate() + 1);
  }
  return touchesWeekend ? 'THE WHOLE WEEKEND' : 'THE WHOLE GATHERING';
}

/** A ticketed, on-platform event the viewer doesn't hold yet — what the all-in charge covers. */
export const isChargeable = (e) => e.ticketPriceCents > 0 && !e.isExternalTicketing && !e.viewerHasTicket;
export const isTicketed = (e) => e.ticketPriceCents > 0 && !e.isExternalTicketing;

/** `$35.00` · `FREE` · `EXTERNAL` */
export const priceLabel = (e) => (e.isExternalTicketing ? 'EXTERNAL' : e.ticketPriceCents > 0 ? money(e.ticketPriceCents) : 'FREE');

/** Sort + group an agenda by local day → `[{ key, label, events }]`. */
export function groupByDay(events) {
  const days = [];
  [...events].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime)).forEach((e) => {
    const k = dayKey(e.dateTime);
    let day = days.find((x) => x.key === k);
    if (!day) { day = { key: k, label: dayLong(e.dateTime), events: [] }; days.push(day); }
    day.events.push(e);
  });
  return days;
}

/** The all-in card's wording: band, CTA and the short CTA for the phone bar. */
export function takeAllLabels(count, sumCents, startsAt, endsAt) {
  const whole = wholeLabel(startsAt, endsAt);
  const short = count === 1 ? 'GET THE TICKET' : whole === 'THE WHOLE WEEKEND' ? 'TAKE THE WEEKEND' : 'TAKE IT ALL';
  return {
    band: count === 1 ? 'THE LAST TICKET' : whole,
    short,
    cta: `${short} — ${money(sumCents)}`,
  };
}

/** `5 EVENTS · 3 DAYS · SAT 24 ONLY` */
export function agendaSubLabel(eventCount, dayCount, dayFilterShort) {
  const parts = [plural(eventCount, 'EVENT'), plural(dayCount, 'DAY')];
  if (dayFilterShort) parts.push(`${dayFilterShort} ONLY`);
  return parts.join(' · ');
}

/** The host's per-row fill: `28/40 IN` (ticketed → tickets held, free → going) or `N CLICKED THROUGH` for external. */
export function hostFill(e) {
  if (e.isExternalTicketing) return { label: `${e.goingCount || 0} CLICKED THROUGH`, pct: 0 };
  const n = isTicketed(e) ? (e.ticketCount || 0) : (e.goingCount || 0);
  const cap = e.capacity || 0;
  return { label: cap ? `${n}/${cap} IN` : `${n} IN`, pct: cap ? Math.min(100, Math.round((n / cap) * 100)) : 0 };
}

/** The gathering tabs available to this viewer (the active key falls back to `ann`). */
export function availableTabs({ isHost, requiresApplication, giveTakeEnabled }) {
  const tabs = [];
  if (isHost && requiresApplication) tabs.push({ key: 'entry', label: 'ENTRY' });
  if (isHost) tabs.push({ key: 'guests', label: 'GUESTS' });
  tabs.push({ key: 'ann', label: 'ANNOUNCEMENTS' }, { key: 'tasks', label: 'TASKS' });
  if (giveTakeEnabled !== false) tabs.push({ key: 'board', label: 'GIVE / TAKE' });
  tabs.push({ key: 'people', label: 'PEOPLE' });
  return tabs;
}

/** Manager / list status pill: published → SELLING, draft, ended (past `endsAt`). */
export function gatheringStatus(g, now = new Date()) {
  if (g.status === 'published' && g.endsAt && new Date(g.endsAt) < now) return 'ended';
  return g.status === 'published' ? 'selling' : g.status || 'draft';
}
export const STATUS_PILL = {
  selling: { label: 'SELLING', bg: '#22C55E', fg: '#fff' },
  draft: { label: 'DRAFT', bg: '#fff', fg: '#000', ring: '#000' },
  ended: { label: 'ENDED', bg: '#E5E7EB', fg: '#6B7280' },
};
